// Derives the five AHA setup steps for one class from the server's setup view (GET /classes/{id}/aha/setup).
// States: done, you (the coordinator), admin (someone else must act), lock (class completed), na (not applicable).

export const STEP_KEYS = ["center", "enable", "course", "modules", "instructor"];

const APOS = "’"; // typographic apostrophe used across the setup copy

/**
 * @returns {{ steps, current, course, sessionsDone, sessionsTotal, missingInstructors, done, applicable, locked,
 *             sendingPaused }}
 */
export function computeSetupSteps(setup) {
  const course = setup.course || null;
  const sessionsTotal = setup.sessionsTotal || 0;
  const sessionsDone = setup.sessionsWithModules || 0;
  const instructors = setup.instructors || [];
  const missingInstructors = instructors.filter((i) => !i.hasAhaId);
  const centerOk = !!setup.centerReady;
  const off = !centerOk || !setup.classIssuesAha;
  const locked = !!setup.locked || setup.classStatus === "COMPLETED";
  const env = setup.environment || "this";
  const courseVerified = !!course && course.verified !== false;
  const modulesDone = !!course && (!setup.productHasModules || (sessionsTotal > 0 && (setup.sessionsMissingModules || []).length === 0));

  const steps = [];
  steps.push({
    key: "center", title: "Training center is set up for AHA", owner: "admin",
    state: centerOk ? "done" : "admin",
    summary: centerOk ? "Training center and online-course account are on file." : `Your center ${"isn" + APOS}t connected to the AHA yet.`,
  });
  steps.push({
    key: "enable", title: "This class issues AHA eCards", owner: "you",
    state: !centerOk ? "na" : setup.classIssuesAha ? "done" : "you",
    summary: !centerOk ? "Available once your center is set up."
      : setup.classIssuesAha ? "On. Students who pass get an AHA eCard." : "Off. Turn this on to issue AHA eCards.",
  });
  steps.push({
    key: "course", title: "Pick the AHA course", owner: "you",
    state: off ? "na" : !course ? "you" : courseVerified ? "done" : "admin",
    summary: off ? "Turn on AHA eCards first."
      : !course ? "Choose which AHA course this class teaches."
        : courseVerified ? `${course.name}${course.edition ? ` · ${course.edition} edition` : ""}`
          : `${course.name} isn${APOS}t enabled in ${env} yet. An admin can request it from the AHA.`,
  });
  const noCourseYet = !off && !course;
  steps.push({
    key: "modules", title: "Add the modules each session covers", owner: "you", waiting: noCourseYet,
    state: off ? "na" : !course ? "you" : !setup.productHasModules ? "na" : modulesDone ? "done" : "you",
    summary: off ? "Turn on AHA eCards first."
      : !course ? "After you pick the course."
        : !setup.productHasModules ? `This course doesn${APOS}t use modules.`
          : sessionsTotal === 0 ? "Add the class’s sessions, then list the modules each one covers."
            : `${sessionsDone} of ${sessionsTotal} sessions done`,
  });
  steps.push({
    key: "instructor", title: "Instructor has an AHA instructor ID", owner: "admin",
    state: off ? "na" : instructors.length === 0 ? "you" : missingInstructors.length > 0 ? "admin" : "done",
    summary: off ? "Turn on AHA eCards first."
      : instructors.length === 0 ? "Assign an instructor to this class."
        : missingInstructors.length > 0 ? `${missingInstructors.map((i) => i.name).join(", ")} ${missingInstructors.length > 1 ? "are" : "is"} missing an ID`
          : instructors.length > 1 ? "Every instructor has an ID." : "The instructor has an ID.",
  });

  if (locked) steps.forEach((s) => { if (s.state !== "na") s.state = "lock"; });
  const current = steps.find((s) => s.state === "you" && !s.waiting);
  return {
    steps,
    current: current ? current.key : null,
    course,
    sessionsDone,
    sessionsTotal,
    missingInstructors,
    done: steps.filter((s) => s.state === "done").length,
    applicable: steps.filter((s) => s.state !== "na").length,
    locked,
    sendingPaused: setup.sendingEnabled === false,
    centerOk,
  };
}

/** The one status chip for the card header: { label, tone } with tone in lock | pause | admin | ready | plain. */
export function setupChip(result) {
  const hasAdmin = result.steps.some((s) => s.state === "admin");
  if (result.locked) return { label: "Locked: class completed", tone: "lock" };
  if (!result.centerOk || (hasAdmin && !result.current)) return { label: "Blocked: needs admin", tone: "admin" };
  if (result.done === result.applicable && result.applicable > 0) {
    return result.sendingPaused ? { label: "Ready · sending paused", tone: "pause" } : { label: "AHA ready", tone: "ready" };
  }
  return { label: `${result.done} of 5 steps done`, tone: "plain" };
}
